'use client'

import * as React from 'react'
import { clsx } from 'clsx'
import { usePathname } from 'next/navigation'
import { gsap } from 'gsap'
import { Button, ButtonLink } from '../sotano-kit/primitives/button'

export type AnchorTab = {
  label: React.ReactNode
  icon: React.ReactNode
  href: string
  prefetch?: boolean

  onClick?: () => void
  isActive?: never
}

export type ButtonTab = {
  label: React.ReactNode
  icon: React.ReactNode
  isActive: boolean
  onClick: () => void

  href?: never
  prefetch?: never
}

const tabsAreAnchor = (tabs: Tab[]): tabs is AnchorTab[] =>
  tabs[0]?.href !== undefined
const tabsAreButtons = (tabs: Tab[]): tabs is ButtonTab[] =>
  tabs[0]?.href === undefined

export type Tab = AnchorTab | ButtonTab

export const Tabs: React.FC<{
  tabs: Tab[]
  className?: string
  onAnimationStatusChange?: OnAnimationStatusChange
}> = ({ tabs, className, onAnimationStatusChange }) => {
  if (tabsAreAnchor(tabs)) {
    return (
      <AnchorTabs
        tabs={tabs}
        className={className}
        onAnimationStatusChange={onAnimationStatusChange}
      />
    )
  }

  if (tabsAreButtons(tabs)) {
    return (
      <ButtonTabs
        tabs={tabs}
        className={className}
        onAnimationStatusChange={onAnimationStatusChange}
      />
    )
  }

  throw new Error('Tabs must be either all buttons or all anchors')
}

const AnchorTabs: React.FC<{
  tabs: AnchorTab[]
  className?: string
  onAnimationStatusChange?: OnAnimationStatusChange
}> = ({ tabs, className, onAnimationStatusChange }) => {
  const pathname = usePathname()

  const activeTabIndexByRoute = tabs.findIndex((t) => {
    return Boolean(pathname?.startsWith(t.href))
  })
  const activeTabIndex = activeTabIndexByRoute

  const { parentRef, highlighterRef, tabsRef } =
    useHighlighter<HTMLAnchorElement>(activeTabIndex, onAnimationStatusChange)

  return (
    <nav ref={parentRef} className={clsx('relative flex', className)}>
      {/* Tab highlighter */}
      <div
        ref={highlighterRef}
        className="pointer-events-none invisible absolute top-1/2 -translate-y-1/2 rounded-full bg-dark-border-solid opacity-0"
      />

      {tabs.map((tab, index) => {
        const isActive = activeTabIndex === index

        return (
          <ButtonLink
            unstyled
            focusStyled
            key={tab.href}
            prefetch={tab.prefetch}
            href={tab.href}
            // onFocus={() => setHoveredTabIndex(index)}
            // onBlur={() => setHoveredTabIndex(null)}
            // onMouseEnter={() => setHoveredTabIndex(index)}
            // onMouseLeave={() => setHoveredTabIndex(null)}
            ref={(ref) => {
              tabsRef.current.length < tabs.length &&
                ref &&
                tabsRef.current.push(ref)
            }}
            className={clsx(
              'relative z-10 flex h-[28px] cursor-default select-none items-center space-x-1 rounded-full px-3 text-xs transition duration-150 ease-in-out hover:text-dark-dim',
              isActive ? 'text-dark-dim' : 'text-dark-faint'
            )}
          >
            <span
              className={clsx(
                'absolute left-0 h-[14px] w-px bg-dark-border',
                (index === 1 && activeTabIndex === 2) ||
                  (index === 2 && activeTabIndex === 0)
                  ? 'visible opacity-100'
                  : 'invisible opacity-0'
              )}
            />
            <span className="!ml-0">{tab.icon}</span>
            <span>{tab.label}</span>
          </ButtonLink>
        )
      })}
    </nav>
  )
}

const ButtonTabs: React.FC<{
  tabs: ButtonTab[]
  className?: string
  onAnimationStatusChange?: OnAnimationStatusChange
}> = ({ tabs, className, onAnimationStatusChange }) => {
  const activeTabIndex = tabs.findIndex((tab) => tab.isActive)

  const { parentRef, highlighterRef, tabsRef } =
    useHighlighter<HTMLButtonElement>(activeTabIndex, onAnimationStatusChange)

  return (
    <nav ref={parentRef} className={clsx('relative flex', className)}>
      {/* Tab highlighter */}
      <div
        ref={highlighterRef}
        className="pointer-events-none invisible absolute top-1/2 -translate-y-1/2 rounded-full bg-dark-border-solid opacity-0"
      />

      {tabs.map((tab, index) => {
        const isActive = activeTabIndex === index

        return (
          <Button
            unstyled
            focusStyled
            key={index}
            onClick={tab.onClick}
            ref={(ref) => {
              tabsRef.current.length < tabs.length &&
                ref &&
                tabsRef.current.push(ref)
            }}
            className={clsx(
              'relative z-10 flex h-[28px] cursor-default select-none items-center space-x-1 rounded-full px-3 text-xs transition duration-150 ease-in-out hover:text-dark-dim',
              isActive ? 'text-dark-dim' : 'text-dark-faint'
            )}
          >
            <span
              className={clsx(
                'absolute left-0 h-[14px] w-px bg-dark-border',
                (index === 1 && activeTabIndex === 2) ||
                  (index === 2 && activeTabIndex === 0)
                  ? 'visible opacity-100'
                  : 'invisible opacity-0'
              )}
            />
            <span className="!ml-0">{tab.icon}</span>
            <span>{tab.label}</span>
          </Button>
        )
      })}
    </nav>
  )
}

export type OnAnimationStatusChange = (status: 'running' | 'idle') => void

export const useHighlighter = <T extends HTMLButtonElement | HTMLAnchorElement>(
  activeTabIndex: number,
  onAnimationStatusChange?: OnAnimationStatusChange
) => {
  const parentRef = React.useRef<HTMLElement>(null)
  const highlighterRef = React.useRef<HTMLDivElement>(null)
  const tabsRef = React.useRef<T[]>([])
  const isFirstHighlighterStyleMutationRef = React.useRef(true)

  React.useEffect(() => {
    if (!highlighterRef.current || !parentRef.current) return

    let cleanupFunc: () => void = () => undefined

    function repositionHighlighter(opts?: { instant?: boolean }) {
      isFirstHighlighterStyleMutationRef.current = false
      cleanupFunc()
      const element = tabsRef.current[activeTabIndex]
      if (!element) return

      const vars = {
        left: element.offsetLeft + 'px',
        width: element.offsetWidth + 'px',
        height: element.offsetHeight + 'px',
        autoAlpha: 1,
      }

      if (opts?.instant) {
        gsap.set(highlighterRef.current, vars)
      } else {
        const tween = gsap.to(highlighterRef.current, {
          ...vars,
          duration: 0.15,
          ease: 'easeInOut',
          onStart: () => {
            onAnimationStatusChange?.('running')
          },
          onComplete: () => {
            onAnimationStatusChange?.('idle')
          },
        })
        cleanupFunc = () => {
          tween.kill()
        }
      }
    }

    repositionHighlighter({
      instant: isFirstHighlighterStyleMutationRef.current,
    })

    function handleResize() {
      repositionHighlighter({ instant: true })
    }

    let lastWidth = parentRef.current.offsetWidth
    let lastHeight = parentRef.current.offsetHeight
    const ro = new ResizeObserver(([entry]) => {
      const target = entry?.target as HTMLElement

      if (!target) return

      const widthOrHeightChanged =
        target.offsetWidth !== lastWidth || target.offsetHeight !== lastHeight

      if (widthOrHeightChanged) {
        handleResize()
        lastWidth = target.offsetWidth
        lastHeight = target.offsetHeight
      }
    })
    ro.observe(parentRef.current)

    return () => {
      cleanupFunc()
      ro.disconnect()
    }
  }, [activeTabIndex, onAnimationStatusChange])

  return { parentRef, highlighterRef, tabsRef }
}
